import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { api } from '@/App';
import { Building2, Upload, X, Plus, Trash2, KeyRound } from 'lucide-react';

const SettingsGeneral = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    companyName: '',
    companyLogo: null,
    defaultOperationalCost: 0,
    branchOverrides: [], // [{branch_id, cost_per_gr}]
    showNonManagerShares: false,
    pdfShareExceptionBranches: [], // [branch_id]
    publicApiKey: 'pengepul-abg',
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchBranches();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings({
        companyName: response.data.company_name || 'ERP Sarang Burung Walet',
        companyLogo: response.data.company_logo || null,
        defaultOperationalCost: response.data.default_operational_cost || 0,
        branchOverrides: (response.data.branch_operational_overrides || []).map(o => ({
          branch_id: o.branch_id,
          cost_per_gr: o.cost_per_gr
        })),
        showNonManagerShares: !!response.data.show_non_manager_shares,
        pdfShareExceptionBranches: response.data.pdf_share_exception_branches || [],
        publicApiKey: response.data.public_api_key || 'pengepul-abg',
      });
      setLogoPreview(response.data.company_logo || null);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Gagal memuat pengaturan');
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      setBranches(res.data || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  const handleSave = async () => {
    if (!settings.companyName.trim()) {
      toast.error('Nama perusahaan tidak boleh kosong');
      return;
    }
    if (settings.defaultOperationalCost < 0) {
      toast.error('Biaya operasional default tidak boleh negatif');
      return;
    }
    if (!settings.publicApiKey || !settings.publicApiKey.trim()) {
      toast.error('API Secret Key tidak boleh kosong');
      return;
    }
    // Validate overrides
    const seenBranches = new Set();
    for (const ov of settings.branchOverrides) {
      if (!ov.branch_id) {
        toast.error('Pilih cabang untuk setiap pengecualian');
        return;
      }
      if (seenBranches.has(ov.branch_id)) {
        toast.error('Tidak boleh ada cabang duplikat di pengecualian');
        return;
      }
      seenBranches.add(ov.branch_id);
      if (ov.cost_per_gr < 0 || isNaN(ov.cost_per_gr)) {
        toast.error('Nilai biaya override tidak valid');
        return;
      }
    }

    setLoading(true);
    try {
      await api.put('/settings', {
        company_name: settings.companyName,
        company_logo: settings.companyLogo,
        default_operational_cost: parseFloat(settings.defaultOperationalCost),
        branch_operational_overrides: settings.branchOverrides.map(o => ({
          branch_id: o.branch_id,
          cost_per_gr: parseFloat(o.cost_per_gr) || 0
        })),
        show_non_manager_shares: !!settings.showNonManagerShares,
        pdf_share_exception_branches: settings.pdfShareExceptionBranches || [],
        public_api_key: settings.publicApiKey.trim(),
      });
      toast.success('Pengaturan berhasil disimpan');
      window.dispatchEvent(new CustomEvent('settingsUpdated'));
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const validateAndProcessFile = (file) => {
    if (file.size > 2 * 1024 * 1024) { toast.error('Ukuran file maksimal 2MB'); return false; }
    if (!file.type.startsWith('image/')) { toast.error('File harus berupa gambar'); return false; }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setSettings((s) => ({ ...s, companyLogo: base64String }));
      setLogoPreview(base64String);
      toast.success('Logo berhasil diupload');
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) validateAndProcessFile(file);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) validateAndProcessFile(files[0]);
  };

  const handleRemoveLogo = () => {
    setSettings((s) => ({ ...s, companyLogo: '' }));
    setLogoPreview(null);
  };

  const addOverride = () => {
    setSettings((s) => ({
      ...s,
      branchOverrides: [...s.branchOverrides, { branch_id: '', cost_per_gr: 0 }]
    }));
  };

  const updateOverride = (index, field, value) => {
    setSettings((s) => {
      const next = [...s.branchOverrides];
      next[index] = { ...next[index], [field]: value };
      return { ...s, branchOverrides: next };
    });
  };

  const removeOverride = (index) => {
    setSettings((s) => ({
      ...s,
      branchOverrides: s.branchOverrides.filter((_, i) => i !== index)
    }));
  };

  const usedBranchIds = new Set(settings.branchOverrides.map(o => o.branch_id).filter(Boolean));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Pengaturan Umum</h1>
        <p className="text-slate-600 mt-2">Kelola pengaturan aplikasi secara keseluruhan</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Informasi Perusahaan</CardTitle>
          <CardDescription>Pengaturan informasi dasar perusahaan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LEFT: Nama + Biaya Operasional + Overrides */}
            <div className="space-y-5">
              <div>
                <Label htmlFor="companyName">Nama Perusahaan</Label>
                <Input
                  id="companyName"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  placeholder="Nama perusahaan"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Pengecualian Biaya Operasional per Cabang</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOverride}
                    className="h-8 text-xs"
                    data-testid="add-override-btn"
                  >
                    <Plus size={14} className="mr-1" />
                    Tambah
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Cabang yang terdaftar di bawah akan menggunakan biaya operasional kustom (bukan default). Set 0 untuk bebas biaya operasional.
                </p>

                {settings.branchOverrides.length === 0 ? (
                  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 py-4 px-3 text-center text-xs text-slate-500">
                    Belum ada pengecualian. Semua cabang menggunakan biaya default.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="overrides-list">
                    {settings.branchOverrides.map((ov, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2"
                        data-testid={`override-row-${idx}`}
                      >
                        <div className="flex-1">
                          <SearchableSelect
                            value={ov.branch_id || ''}
                            onValueChange={(v) => updateOverride(idx, 'branch_id', v)}
                            options={branches
                              .filter((b) => b.id === ov.branch_id || !usedBranchIds.has(b.id))
                              .map((b) => ({ value: b.id, label: b.name }))}
                            placeholder="Pilih cabang"
                            searchPlaceholder="Ketik nama cabang..."
                            emptyText="Cabang tidak ditemukan."
                            triggerClassName="h-9 font-normal"
                            testId={`override-branch-select-${idx}`}
                          />
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            min="0"
                            step="1000"
                            value={ov.cost_per_gr}
                            onChange={(e) => updateOverride(idx, 'cost_per_gr', e.target.value)}
                            placeholder="Rp/gr"
                            className="h-9 text-right"
                            data-testid={`override-cost-input-${idx}`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => removeOverride(idx)}
                          data-testid={`remove-override-btn-${idx}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Logo Perusahaan */}
            <div>
              <Label htmlFor="companyLogo">Logo Perusahaan</Label>
              <p className="text-sm text-slate-500 mb-3">Logo akan ditampilkan di halaman login dan menu navigasi (Max 2MB)</p>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
                  isDragging ? 'border-teal-500 bg-teal-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400'
                }`}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center gap-4">
                  {logoPreview ? (
                    <div className="relative w-32 h-32 border-2 border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                      <img src={logoPreview} alt="Logo Preview" className="w-full h-full object-contain p-2" />
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-white">
                      <Building2 className="text-slate-400" size={48} />
                    </div>
                  )}

                  <div className="text-center">
                    {isDragging ? (
                      <p className="text-teal-600 font-medium">Lepaskan file di sini...</p>
                    ) : (
                      <>
                        <p className="text-sm text-slate-600 font-medium mb-1">Drag & drop logo di sini, atau</p>
                        <input type="file" id="logoUpload" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('logoUpload').click()}
                          className="flex items-center gap-2 mt-2"
                          data-testid="upload-logo-btn"
                        >
                          <Upload size={16} />
                          Pilih File
                        </Button>
                      </>
                    )}
                  </div>

                  <p className="text-xs text-slate-500 text-center">
                    Format: JPG, PNG, GIF, SVG • Ukuran maksimal: 2MB
                  </p>

                  {logoPreview && !isDragging && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                      className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="remove-logo-btn"
                    >
                      <X size={16} />
                      Hapus Logo
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pengaturan Laporan PDF</CardTitle>
          <CardDescription>
            Konfigurasi tampilan laporan Export PDF Transaksi Cabang
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4 p-4 border rounded-lg bg-slate-50">
            <div className="flex-1">
              <Label htmlFor="show-non-manager-shares" className="text-base font-medium">
                Tampilkan Saham Mitra Selain Pengelola
              </Label>
              <p className="text-sm text-slate-600 mt-1">
                Jika <strong>aktif</strong>: hasil Export PDF akan menampilkan total saham &amp; dividen
                <em> seluruh</em> mitra (Mitra + Mitra Pengelola) pada cabang.
                <br />
                Jika <strong>nonaktif</strong>: hanya menampilkan saham &amp; dividen Mitra Pengelola saja.
              </p>
            </div>
            <Switch
              id="show-non-manager-shares"
              checked={settings.showNonManagerShares}
              onCheckedChange={(checked) =>
                setSettings((s) => ({ ...s, showNonManagerShares: checked }))
              }
              data-testid="toggle-show-non-manager-shares"
            />
          </div>

          {/* Pengecualian Cabang */}
          <div className="mt-4 p-4 border rounded-lg bg-slate-50">
            <Label className="text-base font-medium">
              Pengecualian Cabang — Tetap Tampilkan Semua Saham
            </Label>
            <p className="text-sm text-slate-600 mt-1 mb-3">
              Cabang yang dipilih di sini akan <strong>selalu</strong> menampilkan
              seluruh saham &amp; dividen mitra di PDF, meskipun toggle di atas
              dalam keadaan nonaktif.
            </p>
            {branches.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Belum ada cabang.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {branches.map((b) => {
                  const checked = (settings.pdfShareExceptionBranches || []).includes(b.id);
                  return (
                    <label
                      key={b.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        checked ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                      data-testid={`exception-branch-${b.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSettings((s) => {
                            const list = new Set(s.pdfShareExceptionBranches || []);
                            if (e.target.checked) list.add(b.id);
                            else list.delete(b.id);
                            return { ...s, pdfShareExceptionBranches: [...list] };
                          });
                        }}
                        className="h-4 w-4 accent-teal-600"
                      />
                      <span className="text-sm font-medium">{b.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound size={20} className="text-teal-600" />
            API Publik
          </CardTitle>
          <CardDescription>
            Secret key untuk website lain mengakses API publik (Data Mitra &amp; Laporan Bulanan)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xl space-y-3">
            <div>
              <Label htmlFor="public-api-key">API Secret Key</Label>
              <Input
                id="public-api-key"
                value={settings.publicApiKey}
                onChange={(e) => setSettings({ ...settings, publicApiKey: e.target.value })}
                placeholder="pengepul-abg"
                className="font-mono"
                data-testid="public-api-key-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Website lain wajib mengirim key ini pada header <code className="bg-slate-100 px-1 rounded">X-API-Key</code>.
                Default: <strong>pengepul-abg</strong>. Berlaku untuk kedua API publik.
              </p>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
              <p className="font-medium text-slate-700">Endpoint publik:</p>
              <p><code className="bg-white px-1 rounded border">GET /api/public/mitra</code> — daftar data Mitra</p>
              <p><code className="bg-white px-1 rounded border">GET /api/public/laporan-bulanan?month=YYYY-MM</code> — ringkasan keuangan bulanan</p>
              <p><code className="bg-white px-1 rounded border">GET /api/public/mitra/ai-prompt</code> — prompt AI untuk website lain</p>
              <p className="text-amber-700 mt-1">⚠️ Data KTP/foto bersifat sensitif — bagikan key hanya ke pihak tepercaya.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="save-settings-btn"
        >
          {loading ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsGeneral;
